<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed inset-0 z-[60] overflow-y-auto" @close="emitClose">
      <div class="flex min-h-screen items-center justify-center p-4">
        <TransitionChild
          as="template"
          enter="ease-out duration-200"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-150"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-black bg-opacity-50 backdrop-soft" />
        </TransitionChild>

        <TransitionChild
          as="template"
          enter="ease-out duration-200"
          enter-from="opacity-0 translate-y-2 scale-95"
          enter-to="opacity-100 translate-y-0 scale-100"
          leave="ease-in duration-150"
          leave-from="opacity-100 translate-y-0 scale-100"
          leave-to="opacity-0 translate-y-2 scale-95"
        >
          <div class="relative w-full max-w-3xl shell" style="border-radius: 28px;">
            <!-- HEADER -->
            <header class="shrink-0 shellHead">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <DialogTitle class="text-lg font-semibold text-white tracking-tight">
                    Faktury klienta
                  </DialogTitle>
                  <p class="mt-1 text-xs text-slate-300" v-if="companyName">
                    {{ companyName }}
                  </p>
                </div>

                <button
                  type="button"
                  @click.prevent="emitClose"
                  class="iconBtn shrink-0"
                  aria-label="Zamknij"
                >
                  ✕
                </button>
              </div>
            </header>

            <!-- BODY -->
            <div class="shellBody">
              <div v-if="loading" class="py-10 text-center text-sm text-slate-300">
                Wczytywanie faktur…
              </div>

              <div v-else-if="sortedInvoices.length === 0" class="py-10 text-center">
                <div class="text-sm font-semibold text-slate-100">Brak faktur do wyświetlenia</div>
                <div class="mt-1 text-xs text-slate-400">Ten klient nie ma jeszcze żadnych faktur w systemie.</div>
              </div>

              <div v-else class="overflow-hidden rounded-2xl border border-slate-700">
                <table class="w-full divide-y divide-slate-700">
                  <thead class="bg-slate-700 bg-opacity-70">
                    <tr>
                      <th class="px-3 py-3 text-left text-xs font-semibold text-slate-100">Numer faktury</th>
                      <th class="px-3 py-3 text-left text-xs font-semibold text-slate-100">Status</th>
                      <th class="px-3 py-3 text-left text-xs font-semibold text-slate-100">Termin płatności</th>
                      <th class="px-3 py-3 text-right text-xs font-semibold text-slate-100">Kwota</th>
                      <th class="px-3 py-3 text-right text-xs font-semibold text-slate-100">Opóźnienie</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-700 bg-slate-800 bg-opacity-40">
                    <tr
                      v-for="(row, idx) in sortedInvoices"
                      :key="row.id || row.invoiceName || idx"
                    >
                      <td class="px-3 py-3 text-xs text-slate-100 whitespace-nowrap">
                        <div class="font-semibold">{{ row.invoiceName || '—' }}</div>
                      </td>
                      <td class="px-3 py-3 text-xs whitespace-nowrap">
                        <span class="statusPill" :class="statusPillClass(row._status.tone)">
                          {{ row._status.label }}
                        </span>
                      </td>
                      <td class="px-3 py-3 text-xs whitespace-nowrap dateCell">
                        {{ row.paymentDate || '—' }}
                      </td>
                      <td class="px-3 py-3 text-xs text-right whitespace-nowrap amountCell">
                        {{ formatAmountPLN(row.valueOfInvoice) }}
                      </td>
                      <td class="px-3 py-3 text-xs text-right whitespace-nowrap">
                        <span
                          v-if="row._delay.daysLate > 0"
                          :class="row._delay.isOverdue ? 'delayText delayDanger' : 'delayText delayWarning'"
                        >
                          {{ formatDaysLabel(row._delay.daysLate) }}
                        </span>
                        <span
                          v-else-if="row.paid === true"
                          class="delayText delaySuccess"
                        >
                          W terminie
                        </span>
                        <span v-else class="delayText delayMuted">
                          —
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- FOOTER -->
            <footer class="shellFoot">
              <div class="flex items-center justify-between gap-3">
                <div class="text-[11px] text-slate-400">
                  Łącznie faktur: <span class="font-semibold text-slate-200">{{ sortedInvoices.length }}</span>
                </div>
                <button
                  type="button"
                  class="btnSecondary"
                  @click.prevent="emitClose"
                >
                  Zamknij
                </button>
              </div>
            </footer>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import moment from 'moment'
import { computeDaysLateForInvoice, formatAmountPLN, formatDaysLabel } from '@/utils/paymentRating.js'

export default {
  name: 'ClientInvoicesModal',
  components: { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot },
  props: {
    open: { type: Boolean, default: false },
    invoices: { type: Array, default: () => [] },
    companyName: { type: String, default: '' },
    loading: { type: Boolean, default: false }
  },
  computed: {
    sortedInvoices() {
      const now = new Date()
      const decorated = (this.invoices || [])
        .filter((i) => i && i.active !== false)
        .map((inv) => {
          const delay = computeDaysLateForInvoice(inv, now)
          let status
          if (inv.paid === true) {
            if (delay.wasLate) {
              status = { tone: 'warning', label: 'Opłacona po terminie' }
            } else {
              status = { tone: 'success', label: 'Opłacona w terminie' }
            }
          } else if (delay.isOverdue) {
            status = { tone: 'danger', label: 'Po terminie' }
          } else {
            status = { tone: 'neutral', label: 'Oczekuje na płatność' }
          }
          return {
            ...inv,
            _delay: delay,
            _status: status,
            _dueUnix: inv.paymentDate ? moment(inv.paymentDate, 'DD.MM.YYYY').unix() : 0
          }
        })

      decorated.sort((a, b) => {
        // Najpierw po terminie (rosnąco - najstarsze opóźnienia najpierw), potem opłacone na końcu.
        const aOverdue = a._delay.isOverdue ? 0 : 1
        const bOverdue = b._delay.isOverdue ? 0 : 1
        if (aOverdue !== bOverdue) return aOverdue - bOverdue
        return b._dueUnix - a._dueUnix
      })

      return decorated
    }
  },
  methods: {
    formatAmountPLN,
    formatDaysLabel,
    statusPillClass(tone) {
      if (tone === 'danger') return 'statusDanger'
      if (tone === 'success') return 'statusSuccess'
      if (tone === 'warning') return 'statusWarning'
      return 'statusNeutral'
    },
    emitClose() {
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.shell {
  background: rgba(30, 41, 59, 0.98);
  border: 1px solid rgba(71, 85, 105, 0.85);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

.shellHead {
  padding: 20px 22px 18px;
  border-bottom: 1px solid rgba(71, 85, 105, 0.75);
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95));
}

.shellBody {
  padding: 18px 20px;
  max-height: 60vh;
  overflow-y: auto;
}

.shellFoot {
  padding: 14px 20px 18px;
  border-top: 1px solid rgba(71, 85, 105, 0.75);
  background: rgba(15, 23, 42, 0.8);
}

.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  width: 38px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(51, 65, 85, 0.8);
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 700;
  transition: all 0.18s ease;
}

.iconBtn:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnSecondary {
  min-height: 38px;
  padding: 0 16px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid rgba(100, 116, 139, 0.8);
  background: rgba(51, 65, 85, 0.75);
  color: #f1f5f9;
  transition: all 0.18s ease;
}

.btnSecondary:hover {
  background: rgba(71, 85, 105, 0.95);
}

.statusPill {
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid transparent;
  white-space: nowrap;
}

.statusSuccess {
  color: #a7f3d0;
  background: rgba(6, 78, 59, 0.35);
  border-color: rgba(16, 185, 129, 0.45);
}

.statusWarning {
  color: #fed7aa;
  background: rgba(120, 53, 15, 0.38);
  border-color: rgba(249, 115, 22, 0.55);
}

.statusDanger {
  color: #fecaca;
  background: rgba(127, 29, 29, 0.4);
  border-color: rgba(239, 68, 68, 0.55);
}

.statusNeutral {
  color: #cbd5e1;
  background: rgba(51, 65, 85, 0.65);
  border-color: rgba(100, 116, 139, 0.6);
}

.dateCell {
  color: #e2e8f0;
}

.amountCell {
  color: #f1f5f9;
  font-weight: 600;
}

.delayText {
  font-weight: 600;
}

.delayDanger {
  color: #fca5a5;
  font-weight: 700;
}

.delayWarning {
  color: #fdba74;
}

.delaySuccess {
  color: #86efac;
}

.delayMuted {
  color: rgba(148, 163, 184, 0.85);
  font-weight: 500;
}

.backdrop-soft {
  backdrop-filter: blur(3px);
}
</style>
